import { useEffect, useState } from "react";
import { IoRefreshOutline, IoSparklesOutline } from "react-icons/io5";
import { tapMedium } from "@/utils/haptics";

/**
 * Detects when a new Service Worker has installed and is waiting to take over,
 * then shows a friendly banner with a "Ricarica ora" button.
 * Post-messages skipWaiting() to the SW and reloads on click.
 */
export const UpdateBanner = () => {
  const [waitingReg, setWaitingReg] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    let mounted = true;

    const check = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg || !mounted) return;
        // Already has a waiting worker (updated on last check)
        if (reg.waiting && navigator.serviceWorker.controller) {
          setWaitingReg(reg);
          setVisible(true);
        }
        // Listen for new updates found during this session
        reg.addEventListener("updatefound", () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener("statechange", () => {
            if (newSW.state === "installed" && navigator.serviceWorker.controller && mounted) {
              setWaitingReg(reg);
              setVisible(true);
            }
          });
        });
      } catch { /* ignore */ }
    };
    check();

    // Force an update-check when the tab regains focus (users typically come back after some time)
    const onFocus = async () => {
      if (document.hidden) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg?.update) await reg.update();
      } catch { /* ignore */ }
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);

    // If the browser detects a controller change (activation done) → reload once
    let reloading = false;
    const onControllerChange = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  const applyUpdate = () => {
    tapMedium();
    if (!waitingReg?.waiting) {
      window.location.reload();
      return;
    }
    // Ask the new SW to take over. Once it does, controllerchange fires and we reload.
    waitingReg.waiting.postMessage({ type: "SKIP_WAITING" });
    // Fallback: after a beat, reload anyway.
    setTimeout(() => window.location.reload(), 800);
  };

  const dismiss = () => setVisible(false);

  if (!visible) return null;

  return (
    <div className="tf-update-banner" data-testid="update-banner" role="status" aria-live="polite">
      <div className="tf-update-banner-icon" aria-hidden="true">
        <IoSparklesOutline size={20} />
      </div>
      <div className="tf-update-banner-body">
        <div className="tf-update-banner-title">Nuova versione disponibile</div>
        <div className="tf-update-banner-sub">Ricarica per attivare gli ultimi aggiornamenti.</div>
      </div>
      <button
        type="button"
        data-testid="update-banner-reload"
        className="tf-update-banner-cta"
        onClick={applyUpdate}
      >
        <IoRefreshOutline size={16} />
        Ricarica
      </button>
      <button
        type="button"
        data-testid="update-banner-dismiss"
        className="tf-update-banner-close"
        onClick={dismiss}
        aria-label="Chiudi"
      >
        ×
      </button>
    </div>
  );
};
